import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageSquarePlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import FeedbackForm from './FeedbackForm';

const STORAGE_KEY = 'feedback_btn_pos';
const BTN_SIZE = 48;

function clamp(val, min, max) {
  return Math.min(Math.max(val, min), max);
}

// Version bump — clears old saved positions that were too close to bottom
const POS_VERSION = 'v2';

function getSavedPos() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // If saved position is within 80px of screen bottom, discard it
      if (parsed.version === POS_VERSION) return parsed;
    }
  } catch {}
  return null;
}

function getDefaultPos() {
  // Position FAB above the bottom 100px to avoid overlapping any input bars,
  // bottom navigation bars, or home indicators on all phones.
  // On pages with a message input bar (~56px tall), this gives ~44px clearance above it.
  return {
    x: window.innerWidth - BTN_SIZE - 20,
    y: window.innerHeight - BTN_SIZE - 100,
  };
}

export default function FeedbackButton() {
  const { currentUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(() => getSavedPos() || getDefaultPos());
  const dragging = useRef(false);
  const moved = useRef(false);
  const startPointer = useRef({ x: 0, y: 0 });
  const startPos = useRef({ x: 0, y: 0 });

  // Keep button inside viewport on resize (maintain bottom clearance)
  useEffect(() => {
    const onResize = () => {
      setPos(prev => ({
        x: clamp(prev.x, 0, window.innerWidth - BTN_SIZE),
        y: clamp(prev.y, 0, window.innerHeight - BTN_SIZE - 80),
      }));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onMove = useCallback((clientX, clientY) => {
    if (!dragging.current) return;
    const dx = clientX - startPointer.current.x;
    const dy = clientY - startPointer.current.y;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) moved.current = true;
    const x = clamp(startPos.current.x + dx, 0, window.innerWidth - BTN_SIZE);
    const y = clamp(startPos.current.y + dy, 0, window.innerHeight - BTN_SIZE);
    setPos({ x, y });
  }, []);

  const onEnd = useCallback((x, y) => {
    if (!dragging.current) return;
    dragging.current = false;
    const finalPos = {
      x: clamp(x ?? startPos.current.x, 0, window.innerWidth - BTN_SIZE),
      y: clamp(y ?? startPos.current.y, 0, window.innerHeight - BTN_SIZE),
    };
    setPos(finalPos);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...finalPos, version: POS_VERSION }));
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
    window.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('touchend', onTouchEnd);
  }, []); // eslint-disable-line

  const onMouseMove = useCallback((e) => onMove(e.clientX, e.clientY), [onMove]);
  const onMouseUp = useCallback((e) => onEnd(
    clamp(startPos.current.x + e.clientX - startPointer.current.x, 0, window.innerWidth - BTN_SIZE),
    clamp(startPos.current.y + e.clientY - startPointer.current.y, 0, window.innerHeight - BTN_SIZE),
  ), [onEnd]);
  const onTouchMove = useCallback((e) => { e.preventDefault(); onMove(e.touches[0].clientX, e.touches[0].clientY); }, [onMove]);
  const onTouchEnd = useCallback((e) => {
    const t = e.changedTouches[0];
    onEnd(
      clamp(startPos.current.x + t.clientX - startPointer.current.x, 0, window.innerWidth - BTN_SIZE),
      clamp(startPos.current.y + t.clientY - startPointer.current.y, 0, window.innerHeight - BTN_SIZE),
    );
  }, [onEnd]);

  const onPointerDown = useCallback((e) => {
    if (open) return;
    dragging.current = true;
    moved.current = false;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    startPointer.current = { x: clientX, y: clientY };
    startPos.current = { ...pos };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
  }, [open, pos, onMouseMove, onMouseUp, onTouchMove, onTouchEnd]);

  const handleClick = useCallback(() => {
    if (moved.current) return; // was a drag, not a click
    setOpen(true);
  }, []);

  if (!currentUser) return null;

  return (
    <>
      <button
        onMouseDown={onPointerDown}
        onTouchStart={onPointerDown}
        onClick={handleClick}
        data-testid="feedback-fab-btn"
        title="Report a bug or suggest a feature"
        style={{ left: pos.x, top: pos.y, touchAction: 'none' }}
        className="fixed z-40 bg-[#075E54] text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg hover:bg-[#064E46] transition-colors active:scale-95 select-none cursor-grab active:cursor-grabbing"
      >
        <MessageSquarePlus className="w-5 h-5 pointer-events-none" />
      </button>
      {open && <FeedbackForm onClose={() => setOpen(false)} />}
    </>
  );
}
